/**
 * Geocoding service with caching and debouncing for optimal performance
 * Prevents rate limiting and unnecessary API calls to OpenStreetMap Nominatim
 */

import { EXTERNAL_SERVICES } from '@/data/constants/externalServices'

const GEOCODING_API = EXTERNAL_SERVICES.GEOCODING.API_URL
const CACHE_EXPIRY_TIME = EXTERNAL_SERVICES.GEOCODING.CACHE_EXPIRY_MS
const DEBOUNCE_DELAY = EXTERNAL_SERVICES.GEOCODING.DEBOUNCE_DELAY_MS

// In-memory cache for geocoding results
const geocodingCache = new Map()

// Debounce storage
const debounceTimers = new Map()

/**
 * Check if cached result is still valid
 * @param {Object} cacheEntry - Cached entry with timestamp and data
 * @returns {boolean} True if cache is valid
 */
function isCacheValid(cacheEntry) {
  if (!cacheEntry) return false
  return Date.now() - cacheEntry.timestamp < CACHE_EXPIRY_TIME
}

/**
 * Get geocoding result from cache or localStorage
 * @param {string} locationName - Location name to look up
 * @returns {Object|null} Cached coordinates or null
 */
function getCachedResult(locationName) {
  const cacheKey = locationName.toLowerCase().trim()

  // Check in-memory cache first
  const memoryCache = geocodingCache.get(cacheKey)
  if (isCacheValid(memoryCache)) {
    return memoryCache.data
  }

  // Check localStorage cache
  try {
    const localStorageKey = `geocoding_${cacheKey}`
    const localCache = localStorage.getItem(localStorageKey)
    if (localCache) {
      const parsedCache = JSON.parse(localCache)
      if (isCacheValid(parsedCache)) {
        // Restore to memory cache
        geocodingCache.set(cacheKey, parsedCache)
        return parsedCache.data
      } else {
        // Remove expired cache
        localStorage.removeItem(localStorageKey)
      }
    }
  } catch (error) {
    // Handle quota exceeded and other localStorage errors gracefully
    if (error.name === 'QuotaExceededError' || error.code === 22) {
      // Clear old cache entries if quota exceeded
      try {
        const keys = Object.keys(localStorage)
        const geocodingKeys = keys.filter((key) => key.startsWith('geocoding_'))
        // Remove oldest 50% of geocoding cache
        geocodingKeys.slice(0, Math.floor(geocodingKeys.length / 2)).forEach((key) => {
          localStorage.removeItem(key)
        })
      } catch {
        // If clearing fails, just continue without localStorage cache
      }
    }
    if (import.meta.env.DEV)
      console.warn('Failed to read geocoding cache from localStorage:', error)
  }

  return null
}

/**
 * Store geocoding result in cache and localStorage
 * @param {string} locationName - Location name
 * @param {Array} coordinates - [lat, lng] coordinates
 */
function setCachedResult(locationName, coordinates) {
  const cacheKey = locationName.toLowerCase().trim()
  const cacheEntry = {
    data: coordinates,
    timestamp: Date.now(),
  }

  // Store in memory cache
  geocodingCache.set(cacheKey, cacheEntry)

  // Store in localStorage for persistence
  const localStorageKey = `geocoding_${cacheKey}`
  try {
    localStorage.setItem(localStorageKey, JSON.stringify(cacheEntry))
  } catch (error) {
    // Handle quota exceeded errors gracefully
    if (error.name === 'QuotaExceededError' || error.code === 22) {
      // Try to clear old cache entries
      try {
        const keys = Object.keys(localStorage)
        const geocodingKeys = keys.filter((key) => key.startsWith('geocoding_'))
        // Remove oldest 50% of geocoding cache
        geocodingKeys.slice(0, Math.floor(geocodingKeys.length / 2)).forEach((key) => {
          localStorage.removeItem(key)
        })
        // Retry storing the new entry
        try {
          localStorage.setItem(localStorageKey, JSON.stringify(cacheEntry))
        } catch {
          // If retry fails, just continue without localStorage cache
          // Memory cache is still available
        }
      } catch {
        // If clearing fails, just continue without localStorage cache
      }
    }
    if (import.meta.env.DEV) console.warn('Failed to save geocoding cache to localStorage:', error)
  }
}

/**
 * Debounced geocoding function to prevent rapid successive calls
 * @param {string} locationName - Location name to geocode
 * @param {Function} callback - Callback function to call with results
 */
function debouncedGeocode(locationName, callback) {
  const cacheKey = locationName.toLowerCase().trim()

  // Clear existing timer for this location
  if (debounceTimers.has(cacheKey)) {
    clearTimeout(debounceTimers.get(cacheKey))
  }

  // Set new timer
  const timerId = setTimeout(async () => {
    try {
      const coordinates = await fetchCoordinates(locationName)
      callback(null, coordinates)
    } catch (error) {
      callback(error, null)
    } finally {
      debounceTimers.delete(cacheKey)
    }
  }, DEBOUNCE_DELAY)

  debounceTimers.set(cacheKey, timerId)
}

/**
 * Fetch coordinates from OpenStreetMap Nominatim API
 * @param {string} locationName - Location name to geocode
 * @returns {Promise<Array>} Promise resolving to [lat, lng] coordinates
 */
async function fetchCoordinates(locationName) {
  if (!locationName || typeof locationName !== 'string') {
    throw new Error('Invalid location name provided')
  }

  const trimmedName = locationName.trim()
  if (!trimmedName) {
    throw new Error('Empty location name provided')
  }

  try {
    const response = await fetch(
      `${GEOCODING_API}?q=${encodeURIComponent(trimmedName)}&format=json&limit=1`,
      {
        headers: {
          'User-Agent': 'Project-Pulse-App/1.0',
        },
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
      }
    )

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error(`No results found for location: ${locationName}`)
    }

    const result = data[0]
    if (!result.lat || !result.lon) {
      throw new Error(`Invalid coordinates received for location: ${locationName}`)
    }

    const coordinates = [parseFloat(result.lat), parseFloat(result.lon)]

    // Validate coordinates
    if (Number.isNaN(coordinates[0]) || Number.isNaN(coordinates[1])) {
      throw new Error(`Invalid coordinate values for location: ${locationName}`)
    }

    return coordinates
  } catch (error) {
    console.error('Geocoding API error:', error)
    throw error
  }
}

/**
 * Main geocoding service interface
 */
export const geocodingService = {
  /**
   * Get coordinates for a location with caching and debouncing
   * @param {string} locationName - Location name to geocode
   * @param {Function} onSuccess - Callback for successful result
   * @param {Function} onError - Callback for error
   * @returns {Function} Cleanup function to cancel pending request
   */
  getCoordinates(locationName, onSuccess, onError) {
    if (!locationName) {
      onError(new Error('Location name is required'))
      return () => {}
    }

    // Check cache first
    const cachedResult = getCachedResult(locationName)
    if (cachedResult) {
      onSuccess(cachedResult)
      return () => {}
    }

    // Use debounced geocoding for new requests
    debouncedGeocode(locationName, (error, coordinates) => {
      if (error) {
        onError(error)
        return
      }

      // Cache the result
      setCachedResult(locationName, coordinates)
      onSuccess(coordinates)
    })

    // Return cleanup function
    return () => {
      const cacheKey = locationName.toLowerCase().trim()
      if (debounceTimers.has(cacheKey)) {
        clearTimeout(debounceTimers.get(cacheKey))
        debounceTimers.delete(cacheKey)
      }
    }
  },

  /**
   * Get coordinates synchronously from cache only
   * @param {string} locationName - Location name
   * @returns {Array|null} Cached coordinates or null
   */
  getCachedCoordinates(locationName) {
    return getCachedResult(locationName)
  },

  /**
   * Clear all cached geocoding results
   */
  clearCache() {
    geocodingCache.clear()

    // Clear localStorage cache
    try {
      const keys = Object.keys(localStorage)
      keys.forEach((key) => {
        if (key.startsWith('geocoding_')) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      if (import.meta.env.DEV)
        console.warn('Failed to clear geocoding cache from localStorage:', error)
    }
  },

  /**
   * Get cache statistics for debugging
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      memorySize: geocodingCache.size,
      pendingRequests: debounceTimers.size,
    }
  },
}
